/**
 * FikoScreenshot Dialog Component
 * Screenshot export - Kompakt tasarım
 *
 * @author FikoYT
 * @version 1.3.0
 */

import { Component, Input, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FikoScreenshot, ScreenshotOptions } from './FikoScreenshot';

@Component({
  selector: 'fikoyt-screenshot',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="ss-panel">
      <!-- Önizleme -->
      <div class="ss-preview" *ngIf="previewUrl">
        <img [src]="previewUrl" alt="Önizleme">
        <span class="ss-size">{{ previewWidth }} x {{ previewHeight }}</span>
      </div>

      <!-- Format -->
      <div class="ss-row">
        <label>Format</label>
        <select [(ngModel)]="options.format">
          <option value="png">PNG</option>
          <option value="jpeg">JPEG</option>
        </select>
      </div>

      <!-- Çözünürlük -->
      <div class="ss-row">
        <label>Boyut</label>
        <select [(ngModel)]="options.scale">
          <option [ngValue]="1">1x</option>
          <option [ngValue]="2">2x</option>
          <option [ngValue]="4">4x</option>
        </select>
      </div>

      <!-- Transparan (sadece PNG) -->
      <div class="ss-row" *ngIf="options.format === 'png'">
        <label>Transparan</label>
        <input type="checkbox" [(ngModel)]="options.transparent">
      </div>

      <!-- Arkaplan Rengi (transparan değilse) -->
      <div class="ss-row" *ngIf="!options.transparent || options.format === 'jpeg'">
        <label>Arkaplan</label>
        <input type="color" [(ngModel)]="options.backgroundColor" class="ss-color">
      </div>

      <!-- JPEG Kalitesi -->
      <div class="ss-row" *ngIf="options.format === 'jpeg'">
        <label>Kalite: {{ Math.round(options.quality * 100) }}%</label>
        <input type="range" [(ngModel)]="options.quality" min="0.1" max="1" step="0.05">
      </div>

      <!-- Butonlar -->
      <div class="ss-buttons">
        <button class="ss-btn" (click)="kopyala()" [disabled]="isCopying" title="Panoya Kopyala">
          {{ isCopying ? '...' : '📋' }}
        </button>
        <button class="ss-btn refresh" (click)="onizlemeGuncelle()" title="Yenile">
          📷
        </button>
      </div>

      <!-- Hızlı Butonlar -->
      <div class="ss-quick">
        <button (click)="hizliIndir('png', 1)">PNG</button>
        <button (click)="hizliIndir('png', 2)">PNG 2x</button>
        <button (click)="hizliIndir('jpeg', 1)">JPG</button>
      </div>
    </div>
  `,
  styles: [`
    .ss-panel {
      padding: 8px;
      min-width: 180px;
      max-width: 200px;
      font-size: 11px;
      background: linear-gradient(180deg, rgba(0,40,60,0.95) 0%, rgba(0,20,40,0.98) 100%);
      border-radius: 4px;
    }

    .ss-preview {
      background: rgba(0,0,0,0.4);
      border: 1px solid rgba(0,200,220,0.3);
      border-radius: 4px;
      padding: 4px;
      margin-bottom: 8px;
      text-align: center;
    }

    .ss-preview img {
      max-width: 100%;
      max-height: 80px;
      border-radius: 2px;
    }

    .ss-size {
      display: block;
      margin-top: 4px;
      font-size: 9px;
      color: rgba(0,220,240,0.7);
    }

    .ss-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 6px;
      padding: 4px 6px;
      background: rgba(0,0,0,0.3);
      border-radius: 3px;
      border: 1px solid rgba(0,200,220,0.2);
    }

    .ss-row label {
      color: rgba(0,220,240,0.9);
      font-size: 10px;
      font-weight: 500;
    }

    .ss-row select {
      background: rgba(0,60,80,0.8);
      border: 1px solid rgba(0,200,220,0.4);
      color: #fff;
      padding: 2px 4px;
      border-radius: 3px;
      font-size: 10px;
      outline: none;
      cursor: pointer;
    }

    .ss-row select:focus {
      border-color: rgba(0,220,240,0.8);
    }

    .ss-row input[type="checkbox"] {
      width: 14px;
      height: 14px;
      accent-color: #00dce0;
      cursor: pointer;
    }

    .ss-row input[type="range"] {
      width: 80px;
      height: 4px;
      accent-color: #00dce0;
      cursor: pointer;
    }

    .ss-color {
      width: 28px;
      height: 20px;
      padding: 0;
      border: 1px solid rgba(0,200,220,0.4);
      border-radius: 3px;
      cursor: pointer;
      background: transparent;
    }

    .ss-color::-webkit-color-swatch-wrapper {
      padding: 1px;
    }

    .ss-color::-webkit-color-swatch {
      border: none;
      border-radius: 2px;
    }

    .ss-buttons {
      display: flex;
      gap: 4px;
      margin-bottom: 6px;
      justify-content: flex-end;
    }

    .ss-btn {
      flex: 0;
      padding: 6px 10px;
      border: 1px solid rgba(0,200,220,0.5);
      background: rgba(0,60,80,0.6);
      color: #fff;
      border-radius: 3px;
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
    }

    .ss-btn:hover {
      background: rgba(0,100,120,0.8);
      border-color: rgba(0,220,240,0.8);
    }

    .ss-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .ss-btn.refresh {
      flex: 0;
      padding: 6px 10px;
      font-size: 12px;
    }

    .ss-quick {
      display: flex;
      gap: 3px;
      justify-content: center;
    }

    .ss-quick button {
      padding: 3px 8px;
      border: 1px solid rgba(0,200,220,0.3);
      background: rgba(0,40,60,0.6);
      color: rgba(0,220,240,0.8);
      border-radius: 2px;
      font-size: 9px;
      cursor: pointer;
      transition: all 0.2s;
    }

    .ss-quick button:hover {
      background: rgba(0,80,100,0.8);
      border-color: rgba(0,220,240,0.6);
      color: #fff;
    }
  `]
})
export class FikoScreenshotComponent implements OnDestroy {
  Math = Math;

  // ========== DIALOG PATTERN INPUT'LARI ==========

  private _anaGosterim: any = null;

  @Input()
  public get anaGosterim(): any {
    return this._anaGosterim;
  }
  public set anaGosterim(v: any) {
    this._anaGosterim = v;
    if (v) {
      this.initScreenshotService();
      this.onizlemeGuncelle();
    }
  }

  private _pencere: any;
  private dialogCloseSub: Subscription | null = null;

  @Input()
  public get pencere(): any {
    return this._pencere;
  }
  public set pencere(v: any) {
    this._pencere = v?.instance || v;

    if (this.dialogCloseSub) {
      this.dialogCloseSub.unsubscribe();
      this.dialogCloseSub = null;
    }

    if (this._pencere && this._pencere.onDialogClose) {
      this.dialogCloseSub = this._pencere.onDialogClose.subscribe(() => {
        setTimeout(() => this.temizle(), 0);
      });
    }

    this.initComponent();
  }

  @Input() ayarlar: any = {};
  @Input() parametreler: any = {};

  // ========== COMPONENT STATE ==========

  options: ScreenshotOptions = {
    format: 'png',
    scale: 1,
    transparent: false,
    includeAnnotations: true,
    quality: 0.92,
    backgroundColor: '#1a1a2e',
  };

  previewUrl: string | null = null;
  previewWidth = 0;
  previewHeight = 0;

  isProcessing = false;
  isCopying = false;

  private screenshotService: FikoScreenshot | null = null;

  constructor(private cd: ChangeDetectorRef) {}

  ngOnDestroy(): void {
    this.temizle();
  }

  private initComponent(): void {
    if (this.parametreler?.anaGosterim) {
      this._anaGosterim = this.parametreler.anaGosterim;
    }
    if (this._anaGosterim) {
      this.initScreenshotService();
      this.onizlemeGuncelle();
    }
  }

  private temizle(): void {
    if (this.screenshotService) {
      this.screenshotService.dispose();
      this.screenshotService = null;
    }
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
    }
    if (this.dialogCloseSub) {
      this.dialogCloseSub.unsubscribe();
      this.dialogCloseSub = null;
    }
  }

  private showToast(mesaj: string, tip: 'success' | 'error' | 'warning' = 'success'): void {
    const toast = document.createElement('div');
    toast.textContent = mesaj;
    toast.style.cssText = `
      position: fixed; bottom: 80px; left: 50%; transform: translateX(-50%);
      padding: 8px 16px; border-radius: 4px; color: white; font-size: 12px;
      z-index: 99999; animation: toastFade 1.5s ease-in-out forwards;
      background: ${tip === 'success' ? '#00c8dc' : tip === 'error' ? '#ff4d4f' : '#faad14'};
      border: 1px solid rgba(255,255,255,0.2); box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    `;
    if (!document.getElementById('toast-style')) {
      const style = document.createElement('style');
      style.id = 'toast-style';
      style.textContent = `@keyframes toastFade { 0%,15%,85% { opacity: 1; } 100% { opacity: 0; } }`;
      document.head.appendChild(style);
    }
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 1500);
  }

  private initScreenshotService(): void {
    if (!this._anaGosterim) return;

    const renderer = this._anaGosterim.sunucu;
    const scene = this._anaGosterim.sahneler?.[0];
    const camera = this._anaGosterim.aktifKamera;

    if (renderer && scene && camera) {
      this.screenshotService = new FikoScreenshot(renderer, scene, camera);
    }
  }

  async onizlemeGuncelle(): Promise<void> {
    if (!this.screenshotService) this.initScreenshotService();
    if (!this.screenshotService) return;

    try {
      const result = await this.screenshotService.capture({ format: 'png', scale: 1, transparent: false });
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = result.dataUrl;
      this.previewWidth = result.width;
      this.previewHeight = result.height;
      this.cd.detectChanges();
    } catch (e) {
      console.error('Önizleme hatası:', e);
    }
  }

  async indir(): Promise<void> {
    if (!this.screenshotService || this.isProcessing) return;
    this.isProcessing = true;
    this.cd.detectChanges();

    try {
      await this.screenshotService.download(this.options);
      this.showToast('İndirildi!', 'success');
    } catch (e) {
      this.showToast('Hata!', 'error');
    } finally {
      this.isProcessing = false;
      this.cd.detectChanges();
    }
  }

  async kopyala(): Promise<void> {
    if (!this.screenshotService || this.isCopying) return;
    this.isCopying = true;
    this.cd.detectChanges();

    try {
      const ok = await this.screenshotService.copyToClipboard(this.options);
      this.showToast(ok ? 'Kopyalandı!' : 'Desteklenmiyor', ok ? 'success' : 'warning');
    } catch (e) {
      this.showToast('Hata!', 'error');
    } finally {
      this.isCopying = false;
      this.cd.detectChanges();
    }
  }

  async hizliIndir(format: 'png' | 'jpeg', scale: 1 | 2 | 4): Promise<void> {
    if (!this.screenshotService) return;
    try {
      await this.screenshotService.download({ format, scale, transparent: false, quality: 0.92, includeAnnotations: true });
      this.showToast(`${format.toUpperCase()} ${scale}x`, 'success');
    } catch (e) {
      this.showToast('Hata!', 'error');
    }
  }
}
